/**
 * Client-side mirror of the backend RBAC map (app/core/rbac.py).
 *
 * This drives UX only — hiding/showing controls and route guards. The backend
 * remains the sole real enforcement point. Keep this in sync with the backend.
 */
export const Permission = {
  PAGE_MANAGE: 'page:manage',
  ARTICLE_MANAGE: 'article:manage',
  ARTICLE_PUBLISH: 'article:publish',
  EVENT_MANAGE: 'event:manage',
  RESOURCE_MANAGE: 'resource:manage',
  GALLERY_MANAGE: 'gallery:manage',
  PROFILE_MANAGE: 'profile:manage',
  EVENT_REGISTER: 'event:register',
  RESOURCE_VIEW_MEMBER: 'resource:view_member',
  VOLUNTEER_SCHEDULE_VIEW: 'volunteer:schedule_view',
  DONATION_HISTORY_VIEW: 'donation:history_view',
  USER_MANAGE: 'user:manage',
  SUBMISSION_REVIEW: 'submission:review',
  SUPPORT_REQUEST_VIEW: 'support_request:view',
  DONATION_REPORT_VIEW: 'donation:report_view',
  ANALYTICS_VIEW: 'analytics:view',
  SETTINGS_MANAGE: 'settings:manage',
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

const EDITOR: Permission[] = [
  Permission.PAGE_MANAGE,
  Permission.ARTICLE_MANAGE,
  Permission.ARTICLE_PUBLISH,
  Permission.EVENT_MANAGE,
  Permission.RESOURCE_MANAGE,
  Permission.GALLERY_MANAGE,
];

const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  member: [Permission.PROFILE_MANAGE, Permission.EVENT_REGISTER, Permission.RESOURCE_VIEW_MEMBER],
  volunteer: [Permission.PROFILE_MANAGE, Permission.VOLUNTEER_SCHEDULE_VIEW],
  donor: [Permission.DONATION_HISTORY_VIEW],
  content_editor: EDITOR,
  administrator: [
    ...EDITOR,
    Permission.USER_MANAGE,
    Permission.SUBMISSION_REVIEW,
    Permission.SUPPORT_REQUEST_VIEW,
    Permission.DONATION_REPORT_VIEW,
    Permission.ANALYTICS_VIEW,
    Permission.SETTINGS_MANAGE,
    Permission.PROFILE_MANAGE,
  ],
};

export function permissionsForRoles(roles: string[]): Set<Permission> {
  const set = new Set<Permission>();
  for (const role of roles) {
    for (const p of ROLE_PERMISSIONS[role] ?? []) set.add(p);
  }
  return set;
}

/** True if any of the given roles grants staff/admin access to the dashboard. */
export function isStaff(roles: string[]): boolean {
  return roles.includes('content_editor') || roles.includes('administrator');
}
